import { alpha, type Theme } from "@mui/material/styles";
import type { AppConfiguratorShellStyles, MdTheme } from "../types";

function buildSegmentButton(theme: MdTheme, active: boolean) {
  const t = theme;
  const isDark = theme.palette.mode === "dark";
  const pxToRem = t.functions?.pxToRem ?? ((n: number) => `${n / 16}rem`);
  const rowH = pxToRem(39);
  const base = {
    minHeight: rowH,
    py: 0.75,
    px: 1,
    fontWeight: active ? 700 : 600,
    textTransform: "none" as const,
    borderRadius: `${t.shape?.borderRadius ?? 8}px`,
    lineHeight: 1.25,
  };

  if (active) {
    if (isDark) {
      return {
        ...base,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
        "&:hover": { backgroundColor: theme.palette.primary.dark },
      };
    }
    return {
      ...base,
      color: theme.palette.common.white,
      background: t.functions?.linearGradient?.(
        t.palette.gradients?.dark?.main ?? theme.palette.grey[900],
        t.palette.gradients?.dark?.state ?? theme.palette.grey[800]
      ),
      border: "none",
      "&:hover": { filter: "brightness(1.06)" },
    };
  }

  if (isDark) {
    return {
      ...base,
      color: theme.palette.text.primary,
      borderColor: alpha(theme.palette.text.primary, 0.35),
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
      "&:hover": {
        borderColor: alpha(theme.palette.text.primary, 0.5),
        backgroundColor: alpha(theme.palette.text.primary, 0.12),
      },
    };
  }

  const ink = t.palette.dark?.main ?? theme.palette.grey[900];
  return {
    ...base,
    color: ink,
    borderColor: alpha(theme.palette.common.black, 0.22),
    backgroundColor: theme.palette.common.white,
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.black, 0.04),
      borderColor: alpha(theme.palette.common.black, 0.32),
    },
  };
}

function buildSwitchStyles(theme: MdTheme) {
  const isDark = theme.palette.mode === "dark";
  if (!isDark) {
    return {
      "& .MuiSwitch-switchBase": {
        color: theme.palette.grey[600],
        "&.Mui-checked": {
          color: theme.palette.primary.main,
          "& + .MuiSwitch-track": {
            backgroundColor: alpha(theme.palette.primary.main, 0.5),
            opacity: 1,
          },
        },
      },
      "& .MuiSwitch-track": {
        backgroundColor: alpha(theme.palette.common.black, 0.26),
        opacity: 1,
      },
    };
  }
  return {
    "& .MuiSwitch-switchBase": {
      color: alpha(theme.palette.common.white, 0.55),
      "&.Mui-checked": {
        color: theme.palette.primary.light,
        "& + .MuiSwitch-track": {
          backgroundColor: alpha(theme.palette.primary.main, 0.55),
          opacity: 1,
        },
      },
    },
    "& .MuiSwitch-track": {
      backgroundColor: alpha(theme.palette.common.white, 0.22),
      opacity: 1,
    },
  };
}

export function buildConfiguratorStyles(theme: Theme): AppConfiguratorShellStyles {
  const mdTheme = theme as MdTheme;
  const { palette, transitions, borders } = mdTheme;
  const t = mdTheme;
  const pxToRem = t.functions?.pxToRem ?? ((n: number) => `${n / 16}rem`);
  const configuratorWidth = 360;
  const shell = palette.reportChat;
  const isDark = palette.mode === "dark";

  return {
    panelRoot: (openConfigurator = false) => ({
      position: "fixed",
      top: 0,
      right: openConfigurator ? 0 : -configuratorWidth,
      width: configuratorWidth,
      height: "100vh",
      backgroundColor: isDark ? shell.surface : palette.background.paper,
      color: isDark ? shell.assistantText : palette.text.primary,
      borderLeft: `1px solid ${isDark ? shell.border : palette.divider}`,
      boxShadow: isDark ? shell.shadowDark : shell.shadowComposerLight,
      zIndex: 1301,
      overflowY: "auto",
      overflowX: "hidden",
      padding: `0 ${pxToRem(10)}`,
      transition: transitions.create(
        ["right", "background-color", "color", "border-color", "box-shadow"],
        {
          easing: transitions.easing.easeInOut,
          duration: transitions.duration.standard,
        }
      ),
    }),
    backdrop: {
      zIndex: 1300,
      cursor: "pointer",
      backgroundColor: alpha(
        palette.common.black,
        palette.mode === "dark" ? 0.52 : 0.34
      ),
      transition: transitions.create("opacity", {
        duration: transitions.duration.shortest,
      }),
    },
    headerCloseButton: {
      color: isDark ? alpha(palette.common.white, 0.72) : palette.text.secondary,
      transform: "translateY(5px)",
    },
    divider: {
      borderColor: isDark ? shell.border : palette.divider,
    },
    sidenavColorSwatch: (ownerState = { color: "primary", selected: false }) => {
      const { color, selected } = ownerState;
      const th = mdTheme;
      const idleBorder = isDark ? palette.background.sidenav : palette.common.white;
      const selectedBorder = isDark ? palette.common.white : palette.grey[900];
      return {
        width: 24,
        height: 24,
        padding: 0,
        border: `${borders.borderWidth[1]} solid ${selected ? selectedBorder : idleBorder}`,
        transition: transitions.create("border-color", {
          easing: transitions.easing.sharp,
          duration: transitions.duration.shorter,
        }),
        backgroundImage: th.functions?.linearGradient?.(
          th.palette.gradients?.[color]?.main ?? palette.primary.main,
          th.palette.gradients?.[color]?.state ?? palette.primary.dark
        ),
        "&:not(:last-child)": { mr: 1 },
        "&:hover, &:focus, &:active": {
          borderColor: isDark ? palette.common.white : palette.grey[900],
        },
      };
    },
    sidenavTypeHint: {
      color: palette.text.secondary,
      letterSpacing: "0.04em",
      display: "block",
      mt: 0.25,
    },
    sidenavTypeRow: {
      display: "flex",
      mt: 2,
      mr: 1,
    },
    sidenavTypeMiddle: {
      mx: 1,
      width: "8rem",
      minWidth: "8rem",
    },
    sidenavSegmentButton: (active = false) => buildSegmentButton(mdTheme, active),
    languageButton: (active = false) => ({
      ...buildSegmentButton(mdTheme, active),
      minWidth: "3rem",
      minHeight: "2rem",
    }),
    switch: buildSwitchStyles(mdTheme),
  };
}
